import { useReducer, useCallback } from "react";

// Action types
const ACTION_TYPES = {
  UPDATE_NESTED_VALUE: "UPDATE_NESTED_VALUE",
  SET_SETTINGS: "SET_SETTINGS",
  RESET_SETTINGS: "RESET_SETTINGS",
  MERGE_SETTINGS: "MERGE_SETTINGS",
};

// Helper function to update nested object by path
const updateNestedValue = (obj, path, value) => {
  const keys = path.split(".");
  const newObj = { ...obj };
  let current = newObj;

  // Navigate to the parent of the target key
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!(key in current) || typeof current[key] !== "object") {
      current[key] = {};
    } else {
      current[key] = { ...current[key] };
    }
    current = current[key];
  }

  // Set the final value
  const finalKey = keys[keys.length - 1];
  current[finalKey] = value;

  return newObj;
};

// Reducer function
const styleSettingsReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.UPDATE_NESTED_VALUE:
      return updateNestedValue(state, action.path, action.value);

    case ACTION_TYPES.SET_SETTINGS:
      return action.settings;

    case ACTION_TYPES.RESET_SETTINGS:
      return action.defaultSettings;

    case ACTION_TYPES.MERGE_SETTINGS:
      return {
        ...state,
        ...action.settings,
      };

    default:
      return state;
  }
};

/**
 * Custom hook for managing AI video style settings
 * @param {Object} initialSettings - Initial settings object (optional)
 * @returns {Object} { styleSettings, updateStyleSetting, setStyleSettings, resetStyleSettings, mergeStyleSettings }
 */
const useStyleSettings = (initialSettings = null) => {
  const [styleSettings, dispatch] = useReducer(
    styleSettingsReducer,
    initialSettings
  );

  /**
   * Update a nested style setting by path
   * @param {string} path - Dot-separated path (e.g., "openingSlide.backgroundColor" or "slideItem.title.color")
   * @param {any} value - Value to set
   */
  const updateStyleSetting = useCallback((path, value) => {
    dispatch({
      type: ACTION_TYPES.UPDATE_NESTED_VALUE,
      path,
      value,
    });
  }, []);

  /**
   * Set the entire style settings object
   * @param {Object} settings - Complete settings object
   */
  const setStyleSettings = useCallback((settings) => {
    dispatch({
      type: ACTION_TYPES.SET_SETTINGS,
      settings,
    });
  }, []);

  /**
   * Reset style settings to default
   * @param {Object} defaultSettings - Optional custom default settings
   */
  const resetStyleSettings = useCallback((defaultSettings = null) => {
    dispatch({
      type: ACTION_TYPES.RESET_SETTINGS,
      defaultSettings,
    });
  }, []);

  /**
   * Merge new settings with existing settings
   * @param {Object} settings - Settings to merge
   */
  const mergeStyleSettings = useCallback((settings) => {
    dispatch({
      type: ACTION_TYPES.MERGE_SETTINGS,
      settings,
    });
  }, []);

  /**
   * Handle style change from form inputs (compatible with existing handleStyleChange pattern)
   * Supports both simple and nested updates
   * @param {Event|Object} e - Event object or object with target.name and target.value
   */
  const handleStyleChange = useCallback(
    (e) => {
      const name = e.target?.name || e.name;
      const value = e.target?.value !== undefined ? e.target.value : e.value;

      if (!name) {
        console.warn("handleStyleChange: name is required");
        return;
      }

      // Check if it's a nested path (contains dots)
      if (name.includes(".")) {
        updateStyleSetting(name, value);
      } else {
        // Simple top-level update
        updateStyleSetting(name, value);
      }
    },
    [updateStyleSetting]
  );

  return {
    styleSettings,
    updateStyleSetting,
    setStyleSettings,
    resetStyleSettings,
    mergeStyleSettings,
    handleStyleChange,
  };
};

export default useStyleSettings;
